<template>
  <div class="module-body module-body--no-p filters" ref="resourcesList" id="resourcesList">
    <template v-if="!author && !tag">
      <h3 class="visuallyhidden">Filters</h3>
      <ResourceFilters
        :application-id="applicationId"
        :search-api-key="searchApiKey"
        :index-prefix="indexPrefix"
        :indexes="indexes"
      ></ResourceFilters>
    </template>
  </div>
  <div class="module-body">
    <ol v-if="shouldRender && resources.length > 0" class="resources-index-all">
      <li v-for="resource in resources" :key="resource.id">
        <a
          :href="getResourceUrl(resource)"
          :target="getResourceTarget(resource)"
          class="entry-card hover-bg-grow"
        >
          <figure aria-hidden="true">
            <img
              :srcset="getSourceSet(resource)"
              sizes="
                                    (min-width: 1680px) 491px,
                                    (min-width: 1024px) 30vw,
                                    (min-width: 768px) 45vw,
                                    calc(100vw - 32px)
                                "
              alt=""
            />
          </figure>
          <span class="type-p-3-semibold entry-card-meta" aria-hidden="true">
            <span class="resource-type">{{ getType(resource) }}</span>
            <template v-if="shouldShowDate(resource)">
              <time>{{ resource.postDate }}</time>
              &bull;
            </template>
            <span
              >{{ resource.resConsumptionTime }}
              {{ resource.resConsumptionTimeMeasurement
              }}<template v-if="resource.resConsumptionTime > 1"
                >s</template
              ></span
            >
          </span>
          <h2 class="type-h5">{{ resource.resHeadline }}</h2>
          <p aria-hidden="true" v-if="resource.resSummary">
            {{ resource.resSummary }}
          </p>
        </a>
      </li>
    </ol>
    <Pagination
      :current-page="currentPage"
      :total-pages="totalPages"
      @change-page="goToPage"
    />
  </div>
</template>
<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import axios from "axios";
import Pagination from "./Pagination.vue";
import ResourceFilters from "./ResourceFilters.vue";

const props = defineProps({
  indexes: {
    type: [Array, Object],
    default: () => [],
  },
  applicationId: {
    type: String,
    default: "",
  },
  searchApiKey: {
    type: String,
    default: "",
  },
  indexPrefix: {
    type: String,
    default: "",
  },
  tag: {
    type: String,
    required: false,
  },
  author: {
    type: String,
    required: false,
  },
});

const endpoint = "/api/v2";
const apiToken = "0CqllRTzofNymzrmrlsApOQSVSVBOGmW";
const perPage = 12;
const resources = ref([]);
const totalCount = ref(0);
const currentPage = ref(1);
const resourcesList = ref(null);

const resourcesApi = () => {
  return axios.create({
    baseURL: endpoint,
    headers: {
      Authorization: "Bearer " + apiToken,
      "X-Requested-With": "XMLHttpRequest",
    },
  });
};

const getResources = () => {
  if (activeSections.value.length === 0) {
    resources.value = [];
    totalCount.value = 0;
    return;
  }

  resourcesApi()
    .post("", { query: resourcesQuery.value })
    .then((response) => {
      resources.value = response.data.data.entries;
      totalCount.value = response.data.data.entryCount || 0;
    })
    .catch((error) => {
      console.error("Error fetching resources:", error);
      resources.value = [];
      totalCount.value = 0;
    });
};

const queryOffset = computed(() => {
  return (currentPage.value - 1) * perPage;
});

// Common fields shared between article and link entry types
const commonFields = `
  __typename
  typeHandle
  title
  postDate @formatDateTime(format:"M d, Y")
  resHeadline
  resSummary
  resTagOverride
  resDateVisibility
  resConsumptionTime
  resConsumptionTimeMeasurement
  sectionHandle
  resThumbnail {
    thumbnail1920x1080: url(transform: "v2ResourcesThumnail1920x1080")
    thumbnail1374x773: url(transform: "v2ResourcesThumnail1374x773")
    thumbnail800x450: url(transform: "v2ResourcesThumnail800x450")
  }
`;

const resourcesQuery = computed(() => {
  if (activeSections.value.length === 0) {
    return "";
  }

  const querySections = activeSections.value
    .map((section) => `"${section.section}"`)
    .join(", ");

  const sectionQuery = activeSections.value
    .map(
      (section) => `
        ...on ${section.section}_article_Entry {
          ${commonFields}
          url
          resAuthor {
            ...on authors_Entry {
              authorFirstName
              authorLastName
              authorPosition
            }
          }
        }
        ...on ${section.section}_link_Entry {
          ${commonFields}
          resLink
        }
      `,
    )
    .join("\n");

  // Build filter parameters
  const filterParams = [];

  if (props.tag) {
    filterParams.push(`relatedToTags: [{
      slug: "${props.tag}"
      relatedViaField: "resTags"
    }]`);
  }

  if (props.author) {
    filterParams.push(`relatedToEntries: [{
      slug: "${props.author}"
      relatedViaField: "resAuthor"
    }]`);
  }

  // Format filters for entries() - each on its own line with comma
  const entriesFilters =
    filterParams.length > 0
      ? filterParams.map((param) => `${param},`).join("\n      ")
      : "";

  // Format filters for entryCount() - prepend comma and newline
  const countFilters =
    filterParams.length > 0
      ? `,
      ${filterParams.join(",\n      ")}`
      : "";

  return `{
    entries(
      section: [${querySections}],
      ${entriesFilters}
      limit: ${perPage},
      offset: ${queryOffset.value},
      orderBy: "postDate DESC"
    ) {
      ${sectionQuery}
    }
    entryCount(
      section: [${querySections}]${countFilters}
    )
  }`;
});

const getSourceSet = (resource) => {
  return `${resource?.resThumbnail?.[0]?.thumbnail800x450} 800w, ${resource?.resThumbnail?.[0]?.thumbnail1374x773} 1374w, ${resource?.resThumbnail?.[0]?.thumbnail1920x1080} 1920w`;
};

const getType = (resource) => {
  return resource.resTagOverride
    ? resource.resTagOverride
    : resource.sectionHandle.match(/[A-Z][a-z]+|[0-9]+/g).join(" ");
};

const getResourceUrl = (resource) => {
  let type = getResourceType(resource);
  return type === "link" ? resource.resLink : resource.url;
};

const getResourceType = (resource) => {
  let type = resource.__typename;
  return type.substr(type.length - 10) === "link_Entry" ? "link" : "entry";
};

const getResourceTarget = (resource) => {
  let type = getResourceType(resource);
  return type === "entry" ? "_self" : "_blank";
};

const shouldShowDate = (resource) => {
  // Find the section config for this resource
  const sectionConfig = indexList.value.find(
    (item) => item.section === resource.sectionHandle,
  );
  return sectionConfig?.showDate ?? true; // Default to true if not found
};

// Convert indexes to standardized format (same as ResourceFilters)
const indexList = computed(() => {
  if (Array.isArray(props.indexes) && props.indexes.length > 0) {
    // Check if it's the new array-of-objects format
    if (typeof props.indexes[0] === "object" && props.indexes[0].index) {
      // New format: [{ label: 'Blog', index: 'blog', section: 'resourcesBlog', showDate: true }]
      return props.indexes.map((item) => ({
        index: item.index,
        label: item.label,
        section: item.section,
        showDate: item.showDate ?? true, // Default to true if not specified
      }));
    }
  } else if (
    typeof props.indexes === "object" &&
    !Array.isArray(props.indexes)
  ) {
    // Legacy object format: { 'blog': 'Blog', 'webinars': 'Webinars' }
    return Object.entries(props.indexes).map(([index, label]) => ({
      index,
      label,
      section: null,
      showDate: true, // Default to true for legacy format
    }));
  }
  return [];
});

// Check if component should render
const shouldRender = computed(() => {
  return indexList.value.length > 0;
});

// Track selected filters from URL hash
const selectedFilters = ref([]);

// Computed property for active sections based on selected filters
const activeSections = computed(() => {
  if (selectedFilters.value.length === 0) {
    // No filters selected, return all sections
    return indexList.value;
  }

  // Return only sections that match selected filters
  return indexList.value.filter((item) =>
    selectedFilters.value.includes(item.index),
  );
});

// Parse filters from URL hash
const parseFiltersFromHash = () => {
  const hash = window.location.hash.substring(1); // Remove the # character

  if (hash) {
    // Split by + to get individual filters
    selectedFilters.value = hash.split("+").filter((f) => f.trim());
  } else {
    // No filters in hash, show all
    selectedFilters.value = [];
  }
};

// Pagination computed properties
const totalPages = computed(() => {
  return Math.ceil(totalCount.value / perPage);
});

// Pagination method
const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    getResources();

    // Scroll to the resources list using template ref
    if (resourcesList.value) {
      resourcesList.value.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }
};

// Listen for hash changes
const handleHashChange = () => {
  parseFiltersFromHash();
  currentPage.value = 1; // Reset to first page when filters change
  getResources();
};

// Initialize component
onMounted(() => {
  parseFiltersFromHash();
  getResources();

  // Listen for hash changes
  window.addEventListener("hashchange", handleHashChange);
});

// Cleanup
onBeforeUnmount(() => {
  window.removeEventListener("hashchange", handleHashChange);
});
</script>
